import html2canvas from "html2canvas";
import React from "react";

import { useLocation } from "../../contexts/LocationContext";
import { useRace } from "../../contexts/RaceContext";
import { useYear } from "../../contexts/YearContext";
import { getData, getLeapYears, getMonthsDays } from "../../data";
import Card from "../Card";
import HeatMap2 from "../HeatMap2";
import SelectLocation from "../SelectLocation";
import SelectRace from "../SelectRace";
import SelectYear from "../SelectYear";
import styles from "./HeatMapGraphic.module.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function stringToDivs(string) {
  return string.split(" ").map((w) => <div>{w}</div>);
}

function getDataFiltered(race, year, location) {
  let data = getData();

  data = data.filter((d) => new Date(d.date).getFullYear() === year);

  if (race !== "all") {
    data = data.filter((d) => d.race === race);
  }

  if (location !== "US") {
    data = data.filter((d) => d.state === location);
  }

  return data;
}

function getDataByMonth(data) {
  const months = [];

  for (let i = 1; i <= 12; i++) {
    months[i - 1] = data.filter((d) => parseInt(d.date.split("/")[0]) === i);
    // months[i - 1].sort(
    //   (a, b) => parseInt(a.date.split("/")[1]) - parseInt(b.date.split("/")[1])
    // );
  }

  return months;
}

function getCountByDay(dataByMonth) {
  const response = dataByMonth.map((dm) => {
    const dates = [];

    for (let i = 0; i < dm.length; i++) {
      const [mm, dd, yyyy] = dm[i].date.split("/");
      const date = [yyyy, mm, dd].join("-");

      dates.push(date);
    }

    return dates;
  });

  return response;
}

// function getByWeek(countByDay) {
//   const yearsOffset = getYearsOffset();
//   const yearOffset = yearsOffset[year];

//   const response = countByDay.map((d, i) => {
//     let days = d;

//     if (yearOffset && i === 0) {
//       days = [...new Array(yearOffset).fill(0), ...days]
//     }

//     const month = []

//     while (days.length) {
//       const week = []

//       for (let i = 0; i < 7; i++) {
//         const day = {
//           x: i,
//           y: days.shift() ? days.shift() : 0,
//         }

//         week[i] = day
//       }

//       month.push(week)
//     }

//     return {
//       label: MONTHS[i],
//       value: month
//     }
//   })

//   return response
// }

function getByWeek(countByDay) {
  const response = countByDay.map((arr) => {
    const counts = {};
    arr.forEach((x) => {
      counts[x] = (counts[x] || 0) + 1;
    });

    return counts;
  });

  return response;
}

function getChartData(race, year, location) {
  const data = getDataFiltered(race, year, location);
  const dataByMonth = getDataByMonth(data);
  const countByDay = getCountByDay(dataByMonth);
  const weeks = getByWeek(countByDay);
  const monthsDays = getMonthsDays();

  const response = weeks.map((week) => {
    const values = [];

    for (let i = 0; i < Object.keys(week).length; i++) {
      const person = {
        date: Object.keys(week)[i],
        count: week[Object.keys(week)[i]],
      };
      values.push(person);
    }

    let month = "";
    let days = 0;
    if (Object.keys(week).length) {
      const d = Object.keys(week)[0];
      const m = d.split("-")[1];
      month = MONTHS[parseInt(m) - 1];
      days = monthsDays[parseInt(m) - 1];
    }

    return {
      month,
      days,
      values,
    };
  });

  return response;
}

const HeatMapGraphic = () => {
  const printRef = React.useRef();

  const handleDownloadImage = async () => {
    const element = printRef.current;
    const canvas = await html2canvas(element);

    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");

    if (typeof link.download === "string") {
      link.href = data;
      link.download = "image.jpg";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  const downLoadImage = async () => {
    await handleDownloadImage();
  };

  const race = useRace();
  const year = useYear();
  const isCurrentYear = year === new Date().getFullYear();
  const location = useLocation();

  const data = getChartData(race, year, location);

  let daysWithKill = 0;

  for (let i = 0; i < data.length; i++) {
    for (let j = 0; j < data[i].values.length; j++) {
      daysWithKill++;
    }
  }

  const leapYears = getLeapYears();

  let YEARDAYS = 365;
  if (leapYears.includes(year)) {
    YEARDAYS++;
  }

  let daysWithoutKill = YEARDAYS - daysWithKill;
  if (isCurrentYear) {
    const today = new Date();
    const cmas = new Date(today.getFullYear(), 11, 31);
    if (today.getMonth() == 11 && today.getDate() > 25) {
      cmas.setFullYear(cmas.getFullYear() + 1);
    }
    const one_day = 1000 * 60 * 60 * 24;
    daysWithoutKill = Math.floor(daysWithoutKill - (cmas.getTime() - today.getTime()) / one_day);
  }

  const message = `There ${
    isCurrentYear ? "have been" : "were"
  }  ${daysWithoutKill} days ${
    isCurrentYear ? "so far" : ""
  } in ${year} when police did not kill ${race} in ${location}`;

  return (
    <div className={styles.container} ref={printRef}>
      <Card downLoadImage={downLoadImage} message={message}>
        <div className={styles.content}>
          <div className={styles.text}>
            {stringToDivs(`There ${isCurrentYear ? "have been" : "were"}`)}
            <div className={styles.count}>{daysWithoutKill}</div>
            <div className={styles.paddingLeft}>days</div>
            {stringToDivs(`${isCurrentYear ? "so far" : ""} in`)}
            <div className={styles.filter}>
              <SelectYear withLabel={false} small={true} />
            </div>
            {stringToDivs("when police did not kill")}
            <div className={styles.filter}>
              <SelectRace withLabel={false} small={true} />
            </div>
            {stringToDivs("in")}
            <div className={styles.filter}>
              <SelectLocation withLabel={false} small={true} />
            </div>
          </div>
          <div>
            <div className={styles.subtitle}>
              <div className={styles.text}>
                {stringToDivs(`Killings by police in ${year}`)}
                <div>
                  <div className={styles.zero}>0</div>
                </div>
                <div>
                  <div className={styles.one}>1</div>
                </div>
                <div>
                  <div className={styles.two}>2</div>
                </div>
                <div>
                  <div className={styles.threeplus}>3+</div>
                </div>
              </div>
            </div>
            <div className={styles.parent}>
              {data.map(({ month, values, days }, index) => {
                const leapYears = getLeapYears();

                let february = "02-28";

                if (leapYears.includes(year)) {
                  if (month === "February") {
                    days++;
                  }
                  february = "02-29";
                }

                const dates = [
                  "12-31",
                  "01-31",
                  february,
                  "03-31",
                  "04-30",
                  "05-31",
                  "06-30",
                  "07-31",
                  "08-31",
                  "09-30",
                  "10-31",
                  "11-30",
                ];

                const monthInt = MONTHS.indexOf(month) + 1;
                const y = monthInt === 1 ? year - 1 : year;
                const startDate = `${y}-${dates[monthInt - 1]}`;
                const endDate = `${year}-${monthInt}-${days}`;

                if (!values.length) {
                  const monthsDays = getMonthsDays();
                  return (
                    <div>
                      <HeatMap2
                        empty={true}
                        month={MONTHS[index]}
                        values={[]}
                        startDate={`2022-${index + 1}-01`}
                        endDate={`2022-${index + 1}-${monthsDays[index]}`}
                      />
                    </div>
                  );
                }

                return (
                  <div>
                    <HeatMap2
                      month={month}
                      values={values}
                      startDate={startDate}
                      endDate={endDate}
                    />
                    {/* <HeatMap value={d.value} month={d.label} /> */}
                  </div>
                );
              })}
            </div>
            <div></div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default HeatMapGraphic;
